import type {
  AccessLevel,
  ChecklistStatus,
  DepartureCheck,
  PackingState,
  ScheduleEntry,
  TravelDocument,
} from './schema';
import { normalizeCompleteTravelState } from './complete-travel';

export const CHECKLIST_STATUS_LABELS: Record<ChecklistStatus, string> = {
  planned: '챙길 것',
  to_buy: '구매할 것',
  ready: '준비 완료',
  packed: '가방에 넣음',
};

export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  quick: '바로 꺼냄',
  standard: '보통',
  deep: '가방 안쪽',
};

export const DEPARTURE_PHASE_LABELS: Record<NonNullable<DepartureCheck['phase']>, string> = {
  week_before: 'D-7 · 미리 확인',
  day_before: 'D-1 · 전날',
  leaving: '집을 나서기 전',
  airport: '공항에서',
};

export function createDefaultSchedule(state: PackingState): ScheduleEntry[] {
  const departureDate = state.trip.journey?.departureDate;
  return [
    {
      id: `${state.trip.id}:schedule:departure`,
      date: departureDate,
      kind: 'flight',
      title: '출발편과 터미널 확인',
      place: state.trip.journey?.destination ? `${state.trip.journey.destination} 이동` : undefined,
      completed: false,
    },
    {
      id: `${state.trip.id}:schedule:checkin`,
      date: departureDate,
      kind: 'task',
      title: '온라인 체크인 가능 시각 확인',
      completed: false,
    },
  ];
}

export function createDefaultDocuments(tripId: string): TravelDocument[] {
  return [
    { id: `${tripId}:document:passport`, kind: 'passport', label: '여권·신분증 유효기간', status: 'needed' },
    { id: `${tripId}:document:reservation`, kind: 'reservation', label: '항공·숙소 예약 확인', status: 'needed' },
    { id: `${tripId}:document:insurance`, kind: 'insurance', label: '여행자 보험 필요 여부', status: 'needed' },
    { id: `${tripId}:document:esim`, kind: 'esim', label: '로밍·eSIM 준비', status: 'needed' },
  ];
}

function legacyBagAssignments(state: PackingState) {
  const containerBag = new Map(state.containers.map((container) => [container.id, container.bagId]));
  return new Map(state.placements.flatMap((placement) => {
    const bagId = containerBag.get(placement.containerId);
    return bagId ? [[placement.itemInstanceId, bagId] as const] : [];
  }));
}

export function normalizeReadinessState(state: PackingState): PackingState {
  const validBagIds = new Set(state.bags.map((bag) => bag.id));
  const legacyAssignments = legacyBagAssignments(state);
  return normalizeCompleteTravelState({
    ...state,
    trip: {
      ...state.trip,
      scheduleEntries: state.trip.scheduleEntries ?? createDefaultSchedule(state),
      travelDocuments: state.trip.travelDocuments ?? createDefaultDocuments(state.trip.id),
    },
    items: state.items.map((item) => {
      const assignedBagId = validBagIds.has(item.assignedBagId ?? '')
        ? item.assignedBagId
        : legacyAssignments.get(item.id);
      return {
        ...item,
        assignedBagId,
        checklistStatus: item.checklistStatus ?? (assignedBagId ? 'packed' : 'planned'),
        accessLevel: item.accessLevel ?? (item.tags.includes('first_access') ? 'quick' : 'standard'),
      };
    }),
  });
}

export function assignItemToBag(state: PackingState, itemId: string, bagId?: string): PackingState {
  if (bagId && !state.bags.some((bag) => bag.id === bagId)) throw new Error(`unknown bag:${bagId}`);
  return {
    ...state,
    items: state.items.map((item) => item.id === itemId ? {
      ...item,
      assignedBagId: bagId,
      checklistStatus: item.checklistStatus,
      placementId: undefined,
      manuallyUnplaced: undefined,
    } : item),
    placements: state.placements.filter((placement) => placement.itemInstanceId !== itemId),
  };
}

export function setChecklistStatus(state: PackingState, itemId: string, checklistStatus: ChecklistStatus): PackingState {
  return {
    ...state,
    items: state.items.map((item) => item.id === itemId ? { ...item, checklistStatus } : item),
  };
}

export function setAccessLevel(state: PackingState, itemId: string, accessLevel: AccessLevel): PackingState {
  return {
    ...state,
    items: state.items.map((item) => item.id === itemId ? { ...item, accessLevel } : item),
  };
}

export interface BagLoad {
  bagId: string;
  itemWeightG: number;
  packedItemWeightG: number;
  totalWeightG: number;
  packedTotalWeightG: number;
  allowanceG?: number;
  remainingG?: number;
  overweight: boolean;
  unknownItems: number;
  groupUnknownItems?: number;
  allowanceGroupId?: string;
  groupTotalWeightG?: number;
  groupAllowanceG?: number;
}

export function deriveBagLoads(state: PackingState): BagLoad[] {
  const normalized = normalizeReadinessState(state);
  const totals = new Map(normalized.bags.map((bag) => {
    const assigned = normalized.items.filter((item) => item.assignedBagId === bag.id);
    const known = assigned.filter((item) => item.weightG !== undefined);
    const itemWeightG = known.reduce((sum, item) => sum + item.weightG!, 0);
    const packedItemWeightG = known.filter((item) => item.checklistStatus === 'packed').reduce((sum, item) => sum + item.weightG!, 0);
    return [bag.id, { itemWeightG, packedItemWeightG, unknownItems: assigned.length - known.length, totalWeightG: bag.emptyWeightG + itemWeightG, packedTotalWeightG: bag.emptyWeightG + packedItemWeightG }] as const;
  }));
  const groupTotals = new Map<string, number>();
  const groupLimits = new Map<string, number>();
  const groupUnknown = new Map<string, number>();
  for (const bag of normalized.bags) {
    if (!bag.allowanceGroupId) continue;
    groupTotals.set(bag.allowanceGroupId, (groupTotals.get(bag.allowanceGroupId) ?? 0) + (totals.get(bag.id)?.totalWeightG ?? 0));
    groupUnknown.set(bag.allowanceGroupId, (groupUnknown.get(bag.allowanceGroupId) ?? 0) + totals.get(bag.id)!.unknownItems);
    if (bag.allowanceGroupLimitG) groupLimits.set(bag.allowanceGroupId, bag.allowanceGroupLimitG);
  }
  return [...normalized.bags].sort((a, b) => a.priority - b.priority).map((bag) => {
    const total = totals.get(bag.id)!;
    if (bag.allowanceGroupId) {
      const groupTotalWeightG = groupTotals.get(bag.allowanceGroupId) ?? total.totalWeightG;
      const groupAllowanceG = groupLimits.get(bag.allowanceGroupId);
      const groupUnknownItems = groupUnknown.get(bag.allowanceGroupId) ?? 0;
      return { bagId: bag.id, ...total, allowanceGroupId: bag.allowanceGroupId, groupTotalWeightG, groupAllowanceG, groupUnknownItems, remainingG: groupAllowanceG === undefined || groupUnknownItems > 0 ? undefined : groupAllowanceG - groupTotalWeightG, overweight: groupAllowanceG !== undefined && groupTotalWeightG > groupAllowanceG };
    }
    const allowanceG = bag.confirmedAllowanceG;
    return { bagId: bag.id, ...total, allowanceG, remainingG: allowanceG === undefined || total.unknownItems > 0 ? undefined : allowanceG - total.totalWeightG, overweight: allowanceG !== undefined && total.totalWeightG > allowanceG };
  });
}

export function readinessSummary(state: PackingState) {
  const normalized = normalizeReadinessState(state);
  const itemTotal = normalized.items.length;
  const packedItems = normalized.items.filter((item) => item.checklistStatus === 'packed').length;
  const preparedItems = normalized.items.filter((item) => item.checklistStatus === 'ready' || item.checklistStatus === 'packed').length;
  const toBuy = normalized.items.filter((item) => item.checklistStatus === 'to_buy').length;
  const documents = normalized.trip.travelDocuments ?? [];
  const readyDocuments = documents.filter((item) => item.status === 'ready').length;
  const checks = normalized.trip.departureChecks ?? [];
  const completedChecks = checks.filter((item) => item.checked).length;
  const schedule = normalized.trip.scheduleEntries ?? [];
  const assignedItems = normalized.items.filter((item) => item.assignedBagId).length;
  const itemScore = itemTotal ? preparedItems / itemTotal : 0;
  const bagScore = itemTotal ? assignedItems / itemTotal : 0;
  const documentScore = documents.length ? readyDocuments / documents.length : 1;
  const departureScore = checks.length ? completedChecks / checks.length : 0;
  const progress = Math.round((itemScore * .4 + bagScore * .2 + documentScore * .2 + departureScore * .2) * 100);
  return { progress, itemTotal, packedItems, preparedItems, toBuy, readyDocuments, documentTotal: documents.length, completedChecks, checkTotal: checks.length, scheduleTotal: schedule.length, assignedItems };
}

export function daysUntil(date?: string, now = new Date()) {
  if (!date) return null;
  const target = new Date(`${date}T00:00:00`);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.ceil((target.getTime() - today.getTime()) / 86_400_000);
}

/** Actual recorded units only; a person without assigned items needs a check, not an invented packing list. */
export function participantPackingSummary(state: PackingState) {
  const normalized = normalizeReadinessState(state);
  const people = (normalized.trip.participants ?? []).map((person) => ({ id: person.id, name: person.name, ownerId: person.name }));
  if (normalized.items.some((item) => item.ownerId === 'shared')) people.push({ id: 'shared', name: '공용', ownerId: 'shared' });
  return people.map((person) => {
    const items = normalized.items.filter((item) => item.ownerId === person.ownerId);
    return { ...person, total: items.length, packed: items.filter((item) => item.checklistStatus === 'packed').length, unassigned: items.filter((item) => !item.assignedBagId).length };
  });
}
